import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Button,
  Modal,
  Slide,
  Chip,
  IconButton,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import copy from "copy-to-clipboard";

import { Alert } from "../../common/styled";
import PermissionGate from "../../../guards/PermissionGate";
import { useNotification } from "../../../../contexts/NotificationContext";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as PlusIcon } from "../../../../vendor/_new/icon/sites/plus.svg";
import { ReactComponent as UploadIcon } from "../../../../vendor/_new/icon/sites/upload.svg";
import { ReactComponent as CopyIcon } from "../../../../vendor/_new/icon/sites/copy.svg";
import { ReactComponent as DeleteIcon } from "../../../../vendor/_new/icon/sites/delete.svg";
import { ReactComponent as EditIcon } from "../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as HelpIcon } from "../../../../vendor/_new/icon/navbar/help.svg";
import { ReactComponent as SettingsIcon } from "../../../../vendor/_new/icon/sidebar/settings.svg";
import { ReactComponent as ConfigIcon } from "../../../../vendor/_new/icon/sidebar/services/config.svg";
import { ReactComponent as ProfileIcon } from "../../../../vendor/_new/icon/navbar/account/profile.svg";
import { ReactComponent as KeyIconSvg } from "../../../../vendor/_new/icon/account/key.svg";
import { ReactComponent as WwwIcon } from "../../../../vendor/_new/icon/sites/www.svg";
import { ReactComponent as CertsIcon } from "../../../../vendor/_new/icon/sidebar/certs.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import { SafeTextField, GreenSwitch } from "../../../common/Greens";
import { MenuItem, Select } from "@mui/material";

import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import useIdP from "../../../../hooks/user/useIdP";
import useDomain from "../../../../hooks/user/useDomain";
import useIPRestriction from "../../../../hooks/user/useIPRestriction";
import useSessionSettings from "../../../../hooks/user/useSessionSettings";
import { auth0Config } from "../../../../config";
import {
  validateIpRestrictionEntry,
  validateIpRestrictionDrawerSave,
  validateSessionTimeoutMinutes,
} from "../../../../utils/validation";
import { SaveButton, cancelButtonSx, formSaveButtonSx } from "../../../common/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SecurityIcon from "@mui/icons-material/Security";

const KeyIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" stroke={colors.neutral[400]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const centeredInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    display: "flex",
    alignItems: "center",
  },
  "& input.MuiOutlinedInput-input": {
    paddingTop: "0px",
    paddingBottom: "0px",
    height: "auto",
  },
};

const getStatusColor = (status) => {
  switch (status) {
    case "verified":
      return { bg: "#06991026", text: "#069910", border: "#069910" };
    case "pending":
      return { bg: "#FFF3E0", text: "#E65100", border: "#FFCC80" };
    case "failed":
      return { bg: "#FFEBEE", text: "#C62828", border: "#EF9A9A" };
    default:
      return { bg: colors.neutral[100], text: colors.neutral[700], border: colors.neutral[200] };
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "verified":
      return "Verified";
    case "pending":
      return "Pending Verification";
    case "failed":
      return "Verification Failed";
    default:
      return status;
  }
};

const SSOPage = () => {
  const { user } = useAuth();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  
  // Check permissions
  const canConfigureSSO = hasPermission(PERMISSIONS.SSO_WRITE);
  const {
    cid,
    provider,
    errMsg: ssoErrMsg,
    getProvider,
    createProvider,
    updateProvider,
    deleteProvider,
    setCid,
  } = useIdP();

  const {
    domains,
    loading: domainsLoading,
    errMsg: domainErrMsg,
    getDomains,
    addDomain,
    verifyDomain,
    deleteDomain,
    regenerateToken,
  } = useDomain();

  const {
    settings: sessionSettings,
    errMsg: sessionSettingsErrMsg,
    getSessionSettings,
    updateSessionSettings,
  } = useSessionSettings();

  const {
    config: ipRestrictionConfig,
    errMsg: ipRestrictionErrMsg,
    clientIp,
    getIPRestrictionConfig,
    setIPRestrictionEnabled,
    addIP,
    updateIPDescription,
    removeIP,
    checkClientIP,
  } = useIPRestriction();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const [newDomain, setNewDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(null);
  const [expandedDomain, setExpandedDomain] = useState(null);

  const [newIp, setNewIp] = useState("");
  const [newIpDescription, setNewIpDescription] = useState("");
  const [newIpError, setNewIpError] = useState("");
  const [addingIp, setAddingIp] = useState(false);
  const [editingIpId, setEditingIpId] = useState(null);
  const [editingIpDescription, setEditingIpDescription] = useState("");
  const [localIpRestrictionEnabled, setLocalIpRestrictionEnabled] = useState(false);
  const [savingIpRestriction, setSavingIpRestriction] = useState(false);

  const [localInactivityTimeout, setLocalInactivityTimeout] = useState(null);
  const [localSessionLifetime, setLocalSessionLifetime] = useState("43200");
  const [savingSessionSettings, setSavingSessionSettings] = useState(false);
  const [openInactivityDrawer, setOpenInactivityDrawer] = useState(false);
  const [openSessionLifetimeDrawer, setOpenSessionLifetimeDrawer] = useState(false);
  const [drawerInactivityTimeout, setDrawerInactivityTimeout] = useState(null);
  const [drawerSessionLifetime, setDrawerSessionLifetime] = useState("43200");
  const [openIpRestrictionDrawer, setOpenIpRestrictionDrawer] = useState(false);
  const [drawerIpRestrictionEnabled, setDrawerIpRestrictionEnabled] = useState(false);

  useEffect(() => {
    if (user?.organisation?.idp_connection_id !== undefined) {
      setCid(user?.organisation?.idp_connection_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organisation?.idp_connection_id]);

  useEffect(() => {
    if (cid) {
      getProvider();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  useEffect(() => {
    getDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getIPRestrictionConfig();
    checkClientIP();
    getSessionSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ipRestrictionConfig !== null) {
      setLocalIpRestrictionEnabled(ipRestrictionConfig?.enabled || false);
    }
  }, [ipRestrictionConfig]);

  useEffect(() => {
    if (sessionSettings !== null) {
      setLocalInactivityTimeout(sessionSettings.sessionInactivityTimeout);
      setLocalSessionLifetime(String(sessionSettings.sessionLifetime || 43200));
    }
  }, [sessionSettings]);

  const isClientIpInAllowedList = (() => {
    if (!clientIp || !ipRestrictionConfig?.ipList?.length) return false;
    return ipRestrictionConfig.ipList.some((ipEntry) => {
      if (ipEntry.type === "ip") {
        return ipEntry.value === clientIp;
      }
      if (ipEntry.type === "cidr") {
        const [network, prefixLength] = ipEntry.value.split("/");
        const prefix = parseInt(prefixLength, 10);
        const ipToNum = (ip) => ip.split(".").reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
        const mask = ~((1 << (32 - prefix)) - 1) >>> 0;
        return (ipToNum(clientIp) & mask) === (ipToNum(network) & mask);
      }
      if (ipEntry.type === "range") {
        const [start, end] = ipEntry.value.split("-").map((ip) => ip.trim());
        const ipToNum = (ip) => ip.split(".").reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
        const clientNum = ipToNum(clientIp);
        return clientNum >= ipToNum(start) && clientNum <= ipToNum(end);
      }
      return false;
    });
  })();

  const hasSaml = !!cid;

  const handleOpenDrawer = (editing = false) => {
    setIsEditing(editing);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIsEditing(false);
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    const domainExists = domains.some((d) => d.domain === newDomain.trim());
    if (domainExists) {
      showError("Domain already exists");
      return;
    }
    setAddingDomain(true);
    try {
      const result = await addDomain(newDomain.trim());
      setNewDomain("");
      setExpandedDomain(result.domain);
      showSuccess("Domain added successfully. Please verify ownership by adding the TXT record.");
    } catch (err) {
      showError(err.message || "Failed to add domain");
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    try {
      await deleteDomain(domain);
      showSuccess("Domain removed successfully");
    } catch (err) {
      showError(err.message || "Failed to remove domain");
    }
  };

  const handleVerifyDomain = async (domain) => {
    setVerifyingDomain(domain);
    try {
      const result = await verifyDomain(domain);
      if (result.isVerified) {
        showSuccess("Domain verified successfully!");
        setExpandedDomain(null);
      } else {
        showError("Domain verification failed. Please check your DNS TXT record and try again.");
      }
    } catch (err) {
      showError(err.message || "Verification failed");
    } finally {
      setVerifyingDomain(null);
    }
  };

  const handleRegenerateToken = async (domain) => {
    try {
      await regenerateToken(domain);
      showSuccess("Verification token regenerated");
    } catch (err) {
      showError(err.message || "Failed to regenerate token");
    }
  };

  const handleCopyVerification = (token) => {
    copy(token);
    showSuccess("Verification token copied!");
  };

  const handleDeleteSSO = async () => {
    setDeleting(true);
    await deleteProvider();
    setDeleting(false);
    setOpenDeleteConfirm(false);
    showSuccess("SSO configuration deleted successfully");
  };

  const handleOpenIpRestrictionDrawer = () => {
    setDrawerIpRestrictionEnabled(localIpRestrictionEnabled);
    setNewIpError("");
    setOpenIpRestrictionDrawer(true);
  };

  const handleCloseIpRestrictionDrawer = () => {
    setOpenIpRestrictionDrawer(false);
  };

  const ipRestrictionSaveValid = useMemo(() => {
    const ipCount = ipRestrictionConfig?.ipList?.length || 0;
    return validateIpRestrictionDrawerSave(ipCount).valid;
  }, [ipRestrictionConfig?.ipList?.length]);

  const ipRestrictionListError = useMemo(() => {
    const ipCount = ipRestrictionConfig?.ipList?.length || 0;
    const result = validateIpRestrictionDrawerSave(ipCount);
    return result.valid ? "" : result.message;
  }, [ipRestrictionConfig?.ipList?.length]);

  const sessionLifetimeValid = useMemo(
    () => validateSessionTimeoutMinutes(drawerSessionLifetime, { min: 1440, max: 525600 }).valid,
    [drawerSessionLifetime],
  );

  const inactivityTimeoutValid = useMemo(() => {
    if (drawerInactivityTimeout === null) return true;
    return validateSessionTimeoutMinutes(drawerInactivityTimeout, { min: 15, max: 1440 }).valid;
  }, [drawerInactivityTimeout]);

  const handleSaveIpRestrictionDrawer = async () => {
    const ipCount = ipRestrictionConfig?.ipList?.length || 0;
    const saveCheck = validateIpRestrictionDrawerSave(ipCount);
    if (!saveCheck.valid) {
      return;
    }
    const enabledValue = drawerIpRestrictionEnabled;
    if (enabledValue && !isClientIpInAllowedList) {
      showWarning("Warning: Your current IP is not in the allowed list. You may lock yourself out.");
    }
    setSavingIpRestriction(true);
    try {
      await setIPRestrictionEnabled(enabledValue);
      await checkClientIP();
      setLocalIpRestrictionEnabled(enabledValue);
      showSuccess(enabledValue ? "IP restrictions enabled" : "IP restrictions disabled");
      setOpenIpRestrictionDrawer(false);
    } catch (err) {
      showError(err.message || "Failed to update IP restrictions");
    } finally {
      setSavingIpRestriction(false);
    }
  };

  const handleAddIP = async () => {
    const ipCheck = validateIpRestrictionEntry(newIp);
    if (!ipCheck.valid) {
      setNewIpError(ipCheck.message);
      return;
    }
    setNewIpError("");
    setAddingIp(true);
    try {
      await addIP(newIp.trim(), newIpDescription.trim());
      await checkClientIP();
      setNewIp("");
      setNewIpDescription("");
      showSuccess("IP address added successfully");
    } catch (err) {
      showError(err.message || "Failed to add IP address");
    } finally {
      setAddingIp(false);
    }
  };

  const handleUpdateIPDescription = async (ipId) => {
    try {
      await updateIPDescription(ipId, editingIpDescription);
      setEditingIpId(null);
      setEditingIpDescription("");
      showSuccess("Description updated successfully");
    } catch (err) {
      showError(err.message || "Failed to update description");
    }
  };

  const handleRemoveIP = async (ipId) => {
    try {
      await removeIP(ipId);
      await checkClientIP();
      showSuccess("IP address removed successfully");
    } catch (err) {
      showError(err.message || "Failed to remove IP address");
    }
  };

  const handleOpenInactivityDrawer = () => {
    setDrawerInactivityTimeout(localInactivityTimeout);
    setOpenInactivityDrawer(true);
  };

  const handleCloseInactivityDrawer = () => {
    setOpenInactivityDrawer(false);
  };

  const handleSaveInactivityTimeout = async () => {
    if (drawerInactivityTimeout !== null) {
      const check = validateSessionTimeoutMinutes(drawerInactivityTimeout, { min: 15, max: 1440 });
      if (!check.valid) {
        showError(check.message);
        return;
      }
    }
    const serverInactivity = sessionSettings?.sessionInactivityTimeout ?? null;
    if (drawerInactivityTimeout === serverInactivity) {
      showInfo("No changes to save");
      return;
    }
    setSavingSessionSettings(true);
    try {
      const result = await updateSessionSettings({ sessionInactivityTimeout: drawerInactivityTimeout });
      setLocalInactivityTimeout(result.sessionInactivityTimeout);
      setLocalSessionLifetime(String(result.sessionLifetime || 43200));
      showSuccess("Portal inactivity timeout updated successfully");
      setOpenInactivityDrawer(false);
    } catch (err) {
      showError(err.message || "Failed to update inactivity timeout");
    } finally {
      setSavingSessionSettings(false);
    }
  };

  const handleOpenSessionLifetimeDrawer = () => {
    setDrawerSessionLifetime(localSessionLifetime);
    setOpenSessionLifetimeDrawer(true);
  };

  const handleCloseSessionLifetimeDrawer = () => {
    setOpenSessionLifetimeDrawer(false);
  };

  const handleSaveSessionLifetime = async () => {
    const lifetimeCheck = validateSessionTimeoutMinutes(drawerSessionLifetime, { min: 1440, max: 525600 });
    if (!lifetimeCheck.valid) {
      showError(lifetimeCheck.message);
      return;
    }
    const serverLifetime = sessionSettings?.sessionLifetime ?? 43200;
    const drawerLifetimeNum = parseInt(drawerSessionLifetime, 10);
    if (drawerLifetimeNum === serverLifetime) {
      showInfo("No changes to save");
      return;
    }
    setSavingSessionSettings(true);
    try {
      const result = await updateSessionSettings({ sessionLifetime: drawerLifetimeNum });
      setLocalInactivityTimeout(result.sessionInactivityTimeout);
      setLocalSessionLifetime(String(result.sessionLifetime || 43200));
      showSuccess("Session lifetime updated successfully");
      setOpenSessionLifetimeDrawer(false);
    } catch (err) {
      showError(err.message || "Failed to update session lifetime");
    } finally {
      setSavingSessionSettings(false);
    }
  };

  const getInactivityTimeoutLabel = (value) => {
    if (value === null) return "Disabled";
    switch (value) {
      case 15: return "15 minutes";
      case 30: return "30 minutes";
      case 60: return "1 hour";
      case 240: return "4 hours";
      case 480: return "8 hours";
      case 1440: return "24 hours";
      default: return `${value} minutes`;
    }
  };

  const getSessionLifetimeLabel = (value) => {
    const num = parseInt(value, 10);
    switch (num) {
      case 1440: return "1 day";
      case 10080: return "7 days";
      case 43200: return "30 days";
      case 129600: return "90 days";
      case 525600: return "1 year";
      default: return `${num} minutes`;
    }
  };

  const startEditingIP = (ip) => {
    setEditingIpId(ip._id);
    setEditingIpDescription(ip.description || "");
  };

  const cancelEditingIP = () => {
    setEditingIpId(null);
    setEditingIpDescription("");
  };

  const getIPTypeLabel = (type) => {
    switch (type) {
      case "ip":
        return "Single IP";
      case "cidr":
        return "CIDR";
      case "range":
        return "Range";
      default:
        return type;
    }
  };

  return (
    <PermissionGate permission={PERMISSIONS.SSO_READ} resourceLabel="SSO configuration">
    <>
      <Helmet title="SSO and Login Security" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <V2PageHeader
            title="SSO and Login Security"
            description="Configure identity providers to enable your organization to login to Layerd Portal with SSO."
            sx={{ pb: 0 }}
          />

          {!hasSaml ? (
            <Box
              sx={{
                border: `1px solid ${colors.neutral[200]}`,
                borderRadius: "8px",
                p: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
              }}
            >
              <KeyIcon />
              <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000], mt: 4, mb: 4 }}>
                Enable SSO for your organization
              </Typography>
              <Button
                startIcon={<PlusIcon style={{ color: "#fff" }} />}
                onClick={() => handleOpenDrawer(false)}
                sx={{
                  height: 36,
                  backgroundColor: "#6371F7",
                  border: "1px solid #6371F7",
                  borderRadius: "8px",
                  fontSize: 14,
                  lineHeight: "21px",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: "regular",
                  "&:hover": { backgroundColor: "#4959ee", borderColor: "#4959ee", color: "#fff" },
                }}
              >
                Add Identity Provider
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px solid ${colors.neutral[200]}`,
                borderRadius: "8px",
                p: 4,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack spacing={1}>
                  <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
                    {provider?.name || "SSO Provider"}
                  </Typography>
                  <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                    SAML 2.0 Connection
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <IconButton onClick={() => handleOpenDrawer(true)}>
                    <EditIcon style={{ color: "#6371F7" }} />
                  </IconButton>
                  <IconButton onClick={() => setOpenDeleteConfirm(true)}>
                    <DeleteIcon style={{ color: colors.red[100] }} />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          )}

          <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", p: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000] }}>
                Allowed Domains for Users
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Allowed Domains for Users
                </Typography>
              </Stack>
            </Stack>
            <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 3 }}>
              Users can be invited to your tenant from only the specified domain(s).
              <br />
              You must enter at least one domain and verify ownership via DNS TXT record.
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <SafeTextField
                size="small"
                placeholder="Enter domain (e.g., example.com)"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddDomain()}
                sx={{ width: 300, ...centeredInputSx }}
                disabled={addingDomain}
              />
              <Button
                variant="outlined"
                onClick={handleAddDomain}
                disabled={addingDomain || !newDomain.trim()}
                sx={{
                  height: 40,
                  borderRadius: "8px",
                  borderColor: "#6371F7",
                  color: "#6371F7",
                  fontSize: 14,
                  textTransform: "none",
                  "&:hover": { borderColor: "#4959ee", color: "#4959ee", backgroundColor: "transparent" },
                }}
              >
                {addingDomain ? <CircularProgress size={20} /> : "Add Domain"}
              </Button>
            </Stack>

            {domainsLoading && domains.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : domains.length === 0 ? (
              <Box
                sx={{
                  backgroundColor: colors.neutral[50],
                  border: `1px solid ${colors.neutral[200]}`,
                  borderRadius: "8px",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                  No domains added yet. Add a domain to allow users from that domain to be invited.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {domains.map((domainData) => {
                  const statusColors = getStatusColor(domainData.verificationStatus);
                  const isExpanded = expandedDomain === domainData.domain || domainData.verificationStatus !== "verified";

                  return (
                    <Box
                      key={domainData.domain}
                      sx={{
                        border: `1px solid ${colors.neutral[200]}`,
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          p: 2,
                          backgroundColor: domainData.verificationStatus === "verified" ? colors.neutral[50] : "#FFFBF5",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <WwwIcon style={{ width: 20, height: 20, color: colors.neutral[600] }} />
                          <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[900] }}>
                            {domainData.domain}
                          </Typography>
                          <Chip
                            label={getStatusLabel(domainData.verificationStatus)}
                            size="small"
                            sx={{
                              fontSize: 12,
                              backgroundColor: statusColors.bg,
                              color: statusColors.text,
                              border: `1px solid ${statusColors.border}`,
                              borderRadius: "4px",
                            }}
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {domainData.verificationStatus !== "verified" && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleVerifyDomain(domainData.domain)}
                              disabled={verifyingDomain === domainData.domain}
                              sx={{
                                height: 32,
                                borderRadius: "6px",
                                borderColor: "#6371F7",
                                color: "#6371F7",
                                fontSize: 12,
                                textTransform: "none",
                                minWidth: 80,
                                "&:hover": { borderColor: "#4959ee", color: "#4959ee", backgroundColor: "transparent" },
                              }}
                            >
                              {verifyingDomain === domainData.domain ? <CircularProgress size={16} /> : "Verify"}
                            </Button>
                          )}
                          <Tooltip title="Delete domain">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveDomain(domainData.domain)}
                            >
                              <DeleteIcon style={{ width: 18, height: 18, color: colors.red[100] }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      {isExpanded && domainData.verificationStatus !== "verified" && (
                        <Box sx={{ p: 3, backgroundColor: "#fff", borderTop: `1px solid ${colors.neutral[200]}` }}>
                          <Typography sx={ { fontSize: 14, color: colors.neutral[800], mb: 2 }}>
                            To verify ownership, add a DNS TXT record with the following details:
                          </Typography>

                          <Stack spacing={2}>
                            <Box>
                              <Typography sx={ { fontSize: 12, fontWeight: "medium", color: colors.neutral[600], mb: 1 }}>
                                TXT Record Name
                              </Typography>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  backgroundColor: colors.neutral[100],
                                  border: `1px solid ${colors.neutral[200]}`,
                                  borderRadius: "4px",
                                  p: 1.5,
                                }}
                              >
                                <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: colors.neutral[800], wordBreak: "break-all" }}>
                                  {domainData.txtRecordName}
                                </Typography>
                                <Tooltip title="Copy">
                                  <IconButton onClick={() => handleCopyVerification(domainData.txtRecordName)} size="small">
                                    <CopyIcon style={{ width: 16, height: 16, color: "#6371F7" }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>

                            <Box>
                              <Typography sx={ { fontSize: 12, fontWeight: "medium", color: colors.neutral[600], mb: 1 }}>
                                TXT Record Value
                              </Typography>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  backgroundColor: colors.neutral[100],
                                  border: `1px solid ${colors.neutral[200]}`,
                                  borderRadius: "4px",
                                  p: 1.5,
                                }}
                              >
                                <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: colors.neutral[800], wordBreak: "break-all", flex: 1, mr: 1 }}>
                                  layerd-domain-verification={domainData.verificationToken}
                                </Typography>
                                <Tooltip title="Copy">
                                  <IconButton onClick={() => handleCopyVerification(`layerd-domain-verification=${domainData.verificationToken}`)} size="small">
                                    <CopyIcon style={{ width: 16, height: 16, color: "#6371F7" }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </Stack>

                          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleVerifyDomain(domainData.domain)}
                              disabled={verifyingDomain === domainData.domain}
                              sx={{
                                height: 32,
                                borderRadius: "6px",
                                backgroundColor: "#6371F7",
                                fontSize: 12,
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#4959ee" },
                              }}
                            >
                              {verifyingDomain === domainData.domain ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Check Verification"}
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleRegenerateToken(domainData.domain)}
                              sx={{
                                height: 32,
                                fontSize: 12,
                                color: colors.neutral[600],
                                textTransform: "none",
                                "&:hover": { backgroundColor: colors.neutral[100] },
                              }}
                            >
                              Regenerate Token
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}

            {domainErrMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {domainErrMsg}
              </Alert>
            )}
          </Box>

          <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", p: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000] }}>
                  IP Restrictions
                </Typography>
                <Typography
                  component="a"
                  onClick={handleOpenIpRestrictionDrawer}
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Edit
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  IP Restrictions
                </Typography>
              </Stack>
            </Stack>
            <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 2 }}>
              Allow users access to the portal only from a list of IP addresses and/or IP ranges.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                Current setting:
              </Typography>
              <Chip
                label={localIpRestrictionEnabled ? "Enabled" : "Disabled"}
                size="small"
                sx={{
                  fontSize: 12,
                  backgroundColor: localIpRestrictionEnabled ? "#06991026" : colors.neutral[100],
                  color: localIpRestrictionEnabled ? "#069910" : colors.neutral[700],
                  border: localIpRestrictionEnabled ? "1px solid #069910" : `1px solid ${colors.neutral[300]}`,
                  borderRadius: "4px",
                }}
              />
              {localIpRestrictionEnabled && ipRestrictionConfig?.ipList && (
                <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                  ({ipRestrictionConfig.ipList.length} IP{ipRestrictionConfig.ipList.length !== 1 ? "s" : ""} allowed)
                </Typography>
              )}
            </Stack>
            {clientIp && localIpRestrictionEnabled && (
              <Box sx={{ backgroundColor: "#6371F726", borderRadius: "4px", py: 1, px: 1.5, display: "inline-flex" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InfoIcon style={{ width: 16, height: 16, color: "#6371F7" }} />
                  <Typography sx={ { fontSize: 13, color: "#6371F7" }}>
                    Your current IP: <strong>{clientIp}</strong> {isClientIpInAllowedList ? "(Allowed)" : "(Not in allowed list)"}
                  </Typography>
                </Stack>
              </Box>
            )}
            {ipRestrictionErrMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {ipRestrictionErrMsg}
              </Alert>
            )}
          </Box>

          <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", p: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000] }}>
                  Portal Inactivity Timeout
                </Typography>
                <Typography
                  component="a"
                  onClick={handleOpenInactivityDrawer}
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Edit
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Portal Inactivity Timeout
                </Typography>
              </Stack>
            </Stack>
            <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 2 }}>
              Users who are idle in the portal for this amount of time will be automatically logged out.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                Current setting:
              </Typography>
              <Chip
                label={getInactivityTimeoutLabel(localInactivityTimeout)}
                size="small"
                sx={{
                  fontSize: 12,
                  backgroundColor: localInactivityTimeout === null ? colors.neutral[100] : "#06991026",
                  color: localInactivityTimeout === null ? colors.neutral[700] : "#069910",
                  border: localInactivityTimeout === null ? `1px solid ${colors.neutral[300]}` : "1px solid #069910",
                  borderRadius: "4px",
                }}
              />
            </Stack>
            {sessionSettingsErrMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {sessionSettingsErrMsg}
              </Alert>
            )}
          </Box>

          <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", p: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000] }}>
                  Session Lifetime
                </Typography>
                <Typography
                  component="a"
                  onClick={handleOpenSessionLifetimeDrawer}
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Edit
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HelpIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: "#6371F7", fontSize: 14, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Session Lifetime
                </Typography>
              </Stack>
            </Stack>
            <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 2 }}>
              Users will be required to re-login after this amount of time, regardless of activity.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                Current setting:
              </Typography>
              <Chip
                label={getSessionLifetimeLabel(localSessionLifetime)}
                size="small"
                sx={{
                  fontSize: 12,
                  backgroundColor: "#06991026",
                  color: "#069910",
                  border: "1px solid #069910",
                  borderRadius: "4px",
                }}
              />
            </Stack>
            {sessionSettingsErrMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {sessionSettingsErrMsg}
              </Alert>
            )}
          </Box>
        </Stack>
      </Box>

      <Modal open={openDrawer} onClose={handleCloseDrawer}>
        <Slide direction="left" in={openDrawer} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
              outline: "none",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 4, pt: 4, pb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <KeyIconSvg style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography sx={ { fontSize: 20, fontWeight: "medium", lineHeight: "30px", color: colors.neutral[1000] }}>
                    {isEditing ? "Edit Identity Provider" : "Add Identity Provider"}
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Configure your SAML identity provider settings.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={handleCloseDrawer} sx={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
              <ReactPerfectScrollbar>
                <Formik
                enableReinitialize={true}
                initialValues={{
                  connection_name: provider?.name || "",
                  signin_url: provider?.options?.signInEndpoint || provider?.sso_url || "",
                  signout_url: provider?.options?.signOutEndpoint || "",
                  idp_domains: provider?.options?.domain_aliases?.join(",") || "",
                  cert: provider?.options?.signingCert || provider?.certificate || "",
                }}
                validationSchema={Yup.object().shape({
                  connection_name: Yup.string().required("Connection Name is required"),
                  signin_url: Yup.string().url("Must be a valid URL").required("Sign In URL is required"),
                  signout_url: Yup.string().url("Must be a valid URL"),
                  cert: Yup.string().required("X.509 Certificate is required"),
                })}
                onSubmit={async (values, { setSubmitting, setErrors }) => {
                  try {
                    const payload = {
                      name: values.connection_name,
                      options: {
                        signInEndpoint: values.signin_url,
                        signOutEndpoint: values.signout_url,
                        signingCert: values.cert,
                        domain_aliases: values.idp_domains
                          ? values.idp_domains.split(",").map((d) => d.trim())
                          : [],
                      },
                    };
                    if (isEditing) {
                      await updateProvider(payload);
                      showSuccess("SSO configuration updated successfully");
                    } else {
                      await createProvider(payload);
                      showSuccess("SSO configuration created successfully");
                    }
                    handleCloseDrawer();
                  } catch (err) {
                    setErrors({ submit: err.message || "An error occurred" });
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => {
                  const effectiveConnectionName = (values.connection_name || provider?.name || "YOUR_CONNECTION_NAME").trim();
                  const replyUrl = `https://${auth0Config.domain}/login/callback?connection=${effectiveConnectionName}`;
                  const entityId = `urn:auth0:${auth0Config.domain?.split(".")[0]}:${effectiveConnectionName}`;

                  return (
                  <form onSubmit={handleSubmit}>
                    <Box sx={{ p: 4 }}>
                      <Stack spacing={4}>
                        {errors.submit && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.submit}
                          </Alert>
                        )}
                        {ssoErrMsg && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {ssoErrMsg}
                          </Alert>
                        )}

                        <Box
                          sx={{
                            backgroundColor: colors.neutral[50],
                            borderRadius: "12px",
                            p: 3,
                            border: `1px solid ${colors.neutral[200]}`,
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                            <SettingsIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                            <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
                              General Settings
                            </Typography>
                          </Stack>

                          <Stack spacing={3}>
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <KeyIconSvg style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                  Connection Name *
                                </Typography>
                              </Stack>
                              <SafeTextField
                                fullWidth
                                size="small"
                                name="connection_name"
                                value={values.connection_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(touched.connection_name && errors.connection_name)}
                                helperText={touched.connection_name && errors.connection_name}
                                disabled={hasSaml && isEditing}
                                placeholder="Enter a unique name for this connection"
                                sx={{ ...centeredInputSx, backgroundColor: "#fff" }}
                              />
                            </Box>

                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <WwwIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                  Reply URL (ACS URL)
                                </Typography>
                                <IconButton size="small" onClick={() => { copy(replyUrl); showSuccess("Reply URL copied!"); }}>
                                  <CopyIcon style={{ width: 14, height: 14, color: "#6371F7" }} />
                                </IconButton>
                              </Stack>
                              <SafeTextField
                                fullWidth
                                size="small"
                                value={replyUrl}
                                InputProps={{ readOnly: true }}
                                sx={{ backgroundColor: "#fff", ...centeredInputSx }}
                              />
                              <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                                <InfoIcon style={{ width: 14, height: 14, color: colors.neutral[500] }} />
                                <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>
                                  Use this URL in your Identity Provider configuration
                                </Typography>
                              </Stack>
                            </Box>

                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <InfoIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                  Entity ID
                                </Typography>
                                <IconButton size="small" onClick={() => { copy(entityId); showSuccess("Entity ID copied!"); }}>
                                  <CopyIcon style={{ width: 14, height: 14, color: "#6371F7" }} />
                                </IconButton>
                              </Stack>
                              <SafeTextField
                                fullWidth
                                size="small"
                                value={entityId}
                                InputProps={{ readOnly: true }}
                                sx={{ backgroundColor: "#fff", ...centeredInputSx }}
                              />
                            </Box>
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            backgroundColor: colors.neutral[50],
                            borderRadius: "12px",
                            p: 3,
                            border: `1px solid ${colors.neutral[200]}`,
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                            <ConfigIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                            <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
                              Identity Provider Settings
                            </Typography>
                          </Stack>

                          <Stack spacing={3}>
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <WwwIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                  Sign In URL *
                                </Typography>
                              </Stack>
                              <SafeTextField
                                fullWidth
                                size="small"
                                sanitize={false}
                                name="signin_url"
                                value={values.signin_url}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(touched.signin_url && errors.signin_url)}
                                helperText={touched.signin_url && errors.signin_url}
                                placeholder="https://your-idp.com/sso/saml"
                                sx={{ ...centeredInputSx, backgroundColor: "#fff" }}
                              />
                            </Box>

                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <WwwIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                  Sign Out URL
                                </Typography>
                              </Stack>
                              <SafeTextField
                                fullWidth
                                size="small"
                                sanitize={false}
                                name="signout_url"
                                value={values.signout_url}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(touched.signout_url && errors.signout_url)}
                                helperText={touched.signout_url && errors.signout_url}
                                placeholder="https://your-idp.com/sso/logout"
                                sx={{ ...centeredInputSx, backgroundColor: "#fff" }}
                              />
                            </Box>

                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <CertsIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                  X.509 Certificate *
                                </Typography>
                              </Stack>
                              <SafeTextField
                                fullWidth
                                multiline
                                rows={4}
                                sanitize={false}
                                name="cert"
                                value={values.cert}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(touched.cert && errors.cert)}
                                helperText={touched.cert && errors.cert}
                                placeholder="Paste your certificate here or upload a file"
                                sx={{ backgroundColor: "#fff" }}
                              />
                              <Button
                                component="label"
                                startIcon={<UploadIcon style={{ width: 16, height: 16, color: "#6371F7" }} />}
                                sx={{
                                  mt: 1.5,
                                  color: "#6371F7",
                                  fontSize: 14,
                                  textTransform: "none",
                                  borderRadius: "8px",
                                  border: "1px dashed #6371F7",
                                  px: 2,
                                  py: 1,
                                  "&:hover": { backgroundColor: "#EEF0FE" },
                                }}
                              >
                                Upload Certificate
                                <input
                                  type="file"
                                  hidden
                                  accept=".pem,.crt,.cer"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = (event) => {
                                        const content = event.target?.result;
                                        if (typeof content === "string") {
                                          setFieldValue("cert", content);
                                        }
                                      };
                                      reader.readAsText(file);
                                    }
                                  }}
                                />
                              </Button>
                            </Box>
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            backgroundColor: colors.neutral[50],
                            borderRadius: "12px",
                            p: 3,
                            border: `1px solid ${colors.neutral[200]}`,
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                            <ProfileIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                            <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
                              Login Experience
                            </Typography>
                          </Stack>

                          <Box>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                              <WwwIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
                              <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                                Identity Provider Domains
                              </Typography>
                            </Stack>
                            <SafeTextField
                              fullWidth
                              size="small"
                              name="idp_domains"
                              value={values.idp_domains}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="example.com, company.org"
                              sx={{ ...centeredInputSx, backgroundColor: "#fff" }}
                            />
                            <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                              <InfoIcon style={{ width: 14, height: 14, color: colors.neutral[500] }} />
                              <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>
                                Comma-separated list of domains for this identity provider
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>

                        <Stack direction="row" alignItems="center" spacing={3} sx={{ mt: 2, pt: 3, borderTop: `1px solid ${colors.neutral[200]}`, pr: 4 }}>
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            sx={{
                              height: 44,
                              width: "50%",
                              backgroundColor: "#6371F7",
                              border: "1px solid #6371F7",
                              borderRadius: "8px",
                              fontSize: 14,
                              lineHeight: "21px",
                              color: "#fff",
                              textTransform: "none",
                              fontWeight: "regular",
                              "&:hover": { backgroundColor: "#4959ee", borderColor: "#4959ee", color: "#fff" },
                              "&.Mui-disabled": {
                                color: colors.neutral[700],
                                backgroundColor: colors.secondary[100],
                                borderColor: colors.secondary[100],
                              },
                            }}
                          >
                            {isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : isEditing ? "Save" : "Save"}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleCloseDrawer}
                            sx={{
                              height: 44,
                              width: "50%",
                              borderRadius: "8px",
                              borderColor: colors.neutral[300],
                              color: colors.neutral[700],
                              fontSize: 14,
                              lineHeight: "21px",
                              textTransform: "none",
                              fontWeight: "regular",
                              "&:hover": { borderColor: colors.neutral[400], backgroundColor: "transparent" },
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </form>
                );
                }}
              </Formik>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>

    <Modal open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "#fff",
            borderRadius: "8px",
            p: 4,
            boxShadow: 24,
          }}
        >
          <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000], mb: 2 }}>
            Delete SSO Configuration
          </Typography>
          <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 4 }}>
            Are you sure you want to delete this SSO configuration? This action cannot be undone.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => setOpenDeleteConfirm(false)}
              sx={{
                height: 36,
                borderRadius: "8px",
                borderColor: colors.neutral[300],
                color: colors.neutral[700],
                fontSize: 14,
                textTransform: "none",
                "&:hover": { borderColor: colors.neutral[400], backgroundColor: "transparent" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteSSO}
              disabled={deleting}
              sx={{
                height: 36,
                borderRadius: "8px",
                backgroundColor: colors.red[100],
                fontSize: 14,
                textTransform: "none",
                "&:hover": { backgroundColor: colors.red[200] },
              }}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={openInactivityDrawer} onClose={handleCloseInactivityDrawer}>
        <Slide direction="left" in={openInactivityDrawer} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
              outline: "none",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 4, pt: 4, pb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccessTimeIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography sx={ { fontSize: 20, fontWeight: "medium", lineHeight: "30px", color: colors.neutral[1000] }}>
                    Portal Inactivity Timeout
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Auto-logout users after period of inactivity.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={handleCloseInactivityDrawer} sx={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
              <ReactPerfectScrollbar>
                <Stack spacing={4} justifyContent="space-between" sx={{ height: "100%", pr: 4 }}>
                  <Stack spacing={4}>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                      Users who are idle in the portal for this amount of time will be automatically logged out.
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <GreenSwitch
                        checked={drawerInactivityTimeout !== null}
                        onChange={(e) => setDrawerInactivityTimeout(e.target.checked ? 30 : null)}
                        disabled={savingSessionSettings}
                      />
                      <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>
                        Enable Portal Inactivity Timeout
                      </Typography>
                    </Stack>

                    {drawerInactivityTimeout !== null && (
                      <Box>
                        <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 1 }}>
                          Timeout Duration
                        </Typography>
                        <Select
                          value={drawerInactivityTimeout}
                          onChange={(e) => setDrawerInactivityTimeout(e.target.value)}
                          size="small"
                          disabled={savingSessionSettings}
                          fullWidth
                          sx={{
                            fontSize: 14,
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[300] },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6371F7" },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6371F7" },
                          }}
                        >
                          <MenuItem value={15}>15 minutes</MenuItem>
                          <MenuItem value={30}>30 minutes</MenuItem>
                          <MenuItem value={60}>1 hour</MenuItem>
                          <MenuItem value={240}>4 hours</MenuItem>
                          <MenuItem value={480}>8 hours</MenuItem>
                          <MenuItem value={1440}>24 hours</MenuItem>
                        </Select>
                        <Typography sx={ { fontSize: 12, color: colors.neutral[500], mt: 1 }}>
                          Maximum allowed timeout is 24 hours.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Button
                      onClick={handleSaveInactivityTimeout}
                      disabled={savingSessionSettings || !inactivityTimeoutValid}
                      sx={{
                        height: 44,
                        width: "50%",
                        backgroundColor: "#6371F7",
                        border: "1px solid #6371F7",
                        borderRadius: "8px",
                        fontSize: 14,
                        lineHeight: "21px",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: "regular",
                        "&:hover": { backgroundColor: "#4959ee", borderColor: "#4959ee", color: "#fff" },
                        "&.Mui-disabled": {
                          color: colors.neutral[700],
                          backgroundColor: colors.secondary[100],
                          borderColor: colors.secondary[100],
                        },
                      }}
                    >
                      {savingSessionSettings ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCloseInactivityDrawer}
                      sx={{
                        height: 44,
                        width: "50%",
                        borderRadius: "8px",
                        borderColor: colors.neutral[300],
                        color: colors.neutral[700],
                        fontSize: 14,
                        lineHeight: "21px",
                        textTransform: "none",
                        fontWeight: "regular",
                        "&:hover": { borderColor: colors.neutral[400], backgroundColor: "transparent" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>

      <Modal open={openSessionLifetimeDrawer} onClose={handleCloseSessionLifetimeDrawer}>
        <Slide direction="left" in={openSessionLifetimeDrawer} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
              outline: "none",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 4, pt: 4, pb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccessTimeIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography sx={ { fontSize: 20, fontWeight: "medium", lineHeight: "30px", color: colors.neutral[1000] }}>
                    Session Lifetime
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Set maximum session duration before re-login.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={handleCloseSessionLifetimeDrawer} sx={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
              <ReactPerfectScrollbar>
                <Stack spacing={4} justifyContent="space-between" sx={{ height: "100%", pr: 4 }}>
                  <Stack spacing={4}>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                      Users will be required to re-login after this amount of time, regardless of activity.
                    </Typography>

                    <Box>
                      <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 1 }}>
                        Session Duration
                      </Typography>
                      <Select
                        value={drawerSessionLifetime}
                        onChange={(e) => setDrawerSessionLifetime(e.target.value)}
                        size="small"
                        disabled={savingSessionSettings}
                        fullWidth
                        sx={{
                          fontSize: 14,
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[300] },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6371F7" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6371F7" },
                        }}
                      >
                        <MenuItem value="1440">1 day</MenuItem>
                        <MenuItem value="10080">7 days</MenuItem>
                        <MenuItem value="43200">30 days</MenuItem>
                        <MenuItem value="129600">90 days</MenuItem>
                        <MenuItem value="525600">1 year</MenuItem>
                      </Select>
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Button
                      onClick={handleSaveSessionLifetime}
                      disabled={savingSessionSettings || !sessionLifetimeValid}
                      sx={{
                        height: 44,
                        width: "50%",
                        backgroundColor: "#6371F7",
                        border: "1px solid #6371F7",
                        borderRadius: "8px",
                        fontSize: 14,
                        lineHeight: "21px",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: "regular",
                        "&:hover": { backgroundColor: "#4959ee", borderColor: "#4959ee", color: "#fff" },
                        "&.Mui-disabled": {
                          color: colors.neutral[700],
                          backgroundColor: colors.secondary[100],
                          borderColor: colors.secondary[100],
                        },
                      }}
                    >
                      {savingSessionSettings ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCloseSessionLifetimeDrawer}
                      sx={{
                        height: 44,
                        width: "50%",
                        borderRadius: "8px",
                        borderColor: colors.neutral[300],
                        color: colors.neutral[700],
                        fontSize: 14,
                        lineHeight: "21px",
                        textTransform: "none",
                        fontWeight: "regular",
                        "&:hover": { borderColor: colors.neutral[400], backgroundColor: "transparent" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>

      <Modal open={openIpRestrictionDrawer} onClose={handleCloseIpRestrictionDrawer}>
        <Slide direction="left" in={openIpRestrictionDrawer} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
              outline: "none",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 4, pt: 4, pb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SecurityIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography sx={ { fontSize: 20, fontWeight: "medium", lineHeight: "30px", color: colors.neutral[1000] }}>
                    IP Restrictions
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Restrict portal access to specific IP addresses.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={handleCloseIpRestrictionDrawer} sx={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
              <ReactPerfectScrollbar>
                <Stack spacing={4} justifyContent="space-between" sx={{ height: "100%", pr: 4 }}>
                  <Stack spacing={4}>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                      Allow users access to the portal only from a list of IP addresses and/or IP ranges.
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <GreenSwitch
                        checked={drawerIpRestrictionEnabled}
                        onChange={(e) => setDrawerIpRestrictionEnabled(e.target.checked)}
                        disabled={savingIpRestriction}
                      />
                      <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>
                        Enable IP Restrictions
                      </Typography>
                    </Stack>

                    {drawerIpRestrictionEnabled && !isClientIpInAllowedList && (
                      <Box sx={{ backgroundColor: "#FFF8E1", borderRadius: "4px", py: 1, px: 1.5, display: "inline-flex" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <InfoIcon style={{ width: 16, height: 16, color: "#F57C00" }} />
                          <Typography sx={ { fontSize: 13, color: "#E65100" }}>
                            Your current IP is not in the allowed list. Enabling restrictions may lock you out.
                          </Typography>
                        </Stack>
                      </Box>
                    )}

                    {clientIp && (
                      <Box sx={{ backgroundColor: "#6371F726", borderRadius: "4px", py: 1, px: 1.5, display: "inline-flex" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <InfoIcon style={{ width: 16, height: 16, color: "#6371F7" }} />
                          <Typography sx={ { fontSize: 13, color: "#6371F7" }}>
                            Your current IP: <strong>{clientIp}</strong> {isClientIpInAllowedList ? "(Allowed)" : "(Not in allowed list)"}
                          </Typography>
                        </Stack>
                      </Box>
                    )}

                    <Box>
                      <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 2 }}>
                        Add IP Address / Range / CIDR
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <SafeTextField
                          placeholder="e.g., 192.168.1.100 or 10.0.0.0/24"
                          value={newIp}
                          error={Boolean(newIpError)}
                          helperText={newIpError || undefined}
                          onChange={(e) => {
                            setNewIp(e.target.value);
                            if (newIpError) setNewIpError("");
                          }}
                          onBlur={() => {
                            if (!newIp.trim()) {
                              setNewIpError("");
                              return;
                            }
                            const check = validateIpRestrictionEntry(newIp);
                            setNewIpError(check.valid ? "" : check.message);
                          }}
                          sx={{ ...centeredInputSx, flex: 1 }}
                          size="small"
                        />
                        <SafeTextField
                          placeholder="Description (optional)"
                          value={newIpDescription}
                          onChange={(e) => setNewIpDescription(e.target.value)}
                          sx={{ ...centeredInputSx, flex: 1 }}
                          size="small"
                        />
                        <Button
                          variant="contained"
                          onClick={handleAddIP}
                          disabled={!newIp.trim() || addingIp || !validateIpRestrictionEntry(newIp).valid}
                          sx={{
                            height: 40,
                            borderRadius: "8px",
                            backgroundColor: "#6371F7",
                            color: "#fff",
                            fontSize: 14,
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#4959ee" },
                            "&:disabled": { backgroundColor: colors.neutral[200], color: colors.neutral[500] },
                          }}
                        >
                          {addingIp ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Add"}
                        </Button>
                      </Stack>
                      <Typography sx={ { fontSize: 12, color: colors.neutral[500], mt: 1 }}>
                        Supports: Single IP (192.168.1.100), IP Range (192.168.1.1-192.168.1.255), CIDR (10.0.0.0/24), IPv6
                      </Typography>
                    </Box>

                    {ipRestrictionListError && (
                      <Typography sx={{ fontSize: 12, color: colors.red?.[500] || "#d32f2f" }}>
                        {ipRestrictionListError}
                      </Typography>
                    )}

                    {ipRestrictionConfig?.ipList && ipRestrictionConfig.ipList.length > 0 && (
                      <Box>
                        <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 2 }}>
                          Allowed IPs ({ipRestrictionConfig.ipList.length})
                        </Typography>
                        <Stack spacing={1}>
                          {ipRestrictionConfig.ipList.map((ip) => (
                            <Box
                              key={ip._id}
                              sx={{
                                border: `1px solid ${colors.neutral[200]}`,
                                borderRadius: "8px",
                                p: 2,
                                backgroundColor: colors.neutral[50],
                              }}
                            >
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                                  <Typography sx={{ fontFamily: "monospace", fontSize: 14, fontWeight: "medium", color: colors.neutral[900] }}>
                                    {ip.value}
                                  </Typography>
                                  <Chip
                                    label={getIPTypeLabel(ip.type)}
                                    size="small"
                                    sx={{
                                      fontSize: 11,
                                      backgroundColor: ip.type === "cidr" ? "#E3F2FD" : ip.type === "range" ? "#FFF3E0" : "#06991026",
                                      color: ip.type === "cidr" ? "#1565C0" : ip.type === "range" ? "#E65100" : "#069910",
                                      border: ip.type === "cidr" ? "1px solid #1565C0" : ip.type === "range" ? "1px solid #E65100" : "1px solid #069910",
                                      borderRadius: "4px",
                                    }}
                                  />
                                  {editingIpId === ip._id ? (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                      <SafeTextField
                                        placeholder="Description"
                                        value={editingIpDescription}
                                        onChange={(e) => setEditingIpDescription(e.target.value)}
                                        sx={{ ...centeredInputSx, flex: 1 }}
                                        size="small"
                                      />
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleUpdateIPDescription(ip._id)}
                                        sx={{
                                          height: 32,
                                          minWidth: 60,
                                          fontSize: 12,
                                          backgroundColor: "#6371F7",
                                          textTransform: "none",
                                          "&:hover": { backgroundColor: "#4959ee" },
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={cancelEditingIP}
                                        sx={{
                                          height: 32,
                                          minWidth: 60,
                                          fontSize: 12,
                                          borderColor: colors.neutral[300],
                                          color: colors.neutral[700],
                                          textTransform: "none",
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </Stack>
                                  ) : (
                                    <Typography sx={ { fontSize: 13, color: colors.neutral[600], flex: 1 }}>
                                      {ip.description || "—"}
                                    </Typography>
                                  )}
                                </Stack>
                                {editingIpId !== ip._id && (
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Tooltip title="Edit description">
                                      <IconButton onClick={() => startEditingIP(ip)} size="small">
                                        <EditIcon style={{ width: 16, height: 16, color: colors.neutral[500] }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove IP">
                                      <IconButton onClick={() => handleRemoveIP(ip._id)} size="small">
                                        <DeleteIcon style={{ width: 16, height: 16, color: "#C62828" }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {ipRestrictionConfig?.ipList && ipRestrictionConfig.ipList.length === 0 && (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>
                          No IP addresses added yet. Add IP addresses above to restrict access.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  {!ipRestrictionSaveValid && ipRestrictionListError && (
                    <Typography sx={{ fontSize: 12, color: colors.red?.[500] || "#d32f2f" }}>
                      {ipRestrictionListError}
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <SaveButton
                      onClick={handleSaveIpRestrictionDrawer}
                      disabled={savingIpRestriction || !ipRestrictionSaveValid}
                      loading={savingIpRestriction}
                      label="Save"
                      sx={formSaveButtonSx}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleCloseIpRestrictionDrawer}
                      sx={{
                        ...cancelButtonSx,
                        height: 44,
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>

    </>
    </PermissionGate>
  );
};

export default SSOPage;
